import { isTimeoutError } from "./fetchWithTimeout";

/**
 * Detects if an error is related to network/connectivity issues.
 *
 * Checks for common network error patterns in error messages and types.
 *
 * @param {any} error - The error to check
 * @returns {boolean} True if the error is network-related
 *
 * @example
 * ```typescript
 * try {
 *   await supabase.from("table").select();
 * } catch (error) {
 *   if (isNetworkError(error)) {
 *     toast({ title: "Problème de connexion", ... });
 *   }
 * }
 * ```
 */
export function isNetworkError(error: any): boolean {
  if (!error) return false;

  const errorMessage = error?.message?.toLowerCase() || '';
  const errorName = error?.name?.toLowerCase() || '';

  // Check for common network error patterns
  const networkPatterns = [
    'network',
    'fetch',
    'timeout',
    'timed out',
    'connection',
    'offline',
    'unreachable',
    'failed to fetch',
    'networkerror',
    'err_internet_disconnected',
    'err_network_changed',
  ];

  return (
    networkPatterns.some(pattern =>
      errorMessage.includes(pattern) || errorName.includes(pattern)
    ) ||
    isTimeoutError(error) ||
    error?.code === 'ETIMEDOUT' ||
    error?.code === 'ENOTFOUND' ||
    error?.code === 'ECONNREFUSED'
  );
}

/**
 * Gets a user-friendly error message based on error type.
 *
 * Distinguishes between network errors and other errors,
 * providing appropriate messages and guidance.
 *
 * @param {any} error - The error to get message for
 * @param {string} defaultMessage - Fallback message for non-network errors
 * @returns {{ title: string; description: string; isNetworkError: boolean }}
 *
 * @example
 * ```typescript
 * const errorInfo = getErrorMessage(error, "Impossible de créer le client");
 *
 * toast({
 *   title: errorInfo.title,
 *   description: errorInfo.description,
 *   variant: "destructive",
 * });
 * ```
 */
export function getErrorMessage(
  error: any,
  defaultMessage: string = "Une erreur s'est produite"
): {
  title: string;
  description: string;
  isNetworkError: boolean;
} {
  const isNetwork = isNetworkError(error);

  if (isNetwork) {
    if (isTimeoutError(error)) {
      return {
        title: "Connexion lente",
        description: "La requête a pris trop de temps. Vérifiez votre connexion et réessayez.",
        isNetworkError: true,
      };
    }

    return {
      title: "Problème de connexion",
      description: "Vérifiez votre connexion internet et réessayez.",
      isNetworkError: true,
    };
  }

  // Check for specific Supabase/PostgreSQL errors
  if (error?.code === '23505') {
    return {
      title: "Doublon détecté",
      description: "Cet élément existe déjà.",
      isNetworkError: false,
    };
  }

  if (error?.code === '23503') {
    return {
      title: "Référence invalide",
      description: "L'élément référencé n'existe pas.",
      isNetworkError: false,
    };
  }

  // Return default message
  return {
    title: "Erreur",
    description: error?.message || defaultMessage,
    isNetworkError: false,
  };
}

/**
 * Creates a retry function that can be passed to toast actions.
 *
 * @param {() => Promise<void>} retryFn - The function to retry
 * @returns {() => void} A function that can be used as a toast action
 *
 * @example
 * ```typescript
 * const handleSubmit = async () => {
 *   try {
 *     await createClient(data);
 *   } catch (error) {
 *     const errorInfo = getErrorMessage(error);
 *
 *     toast({
 *       ...errorInfo,
 *       action: errorInfo.isNetworkError ? (
 *         <Button onClick={createRetryAction(handleSubmit)}>
 *           Réessayer
 *         </Button>
 *       ) : undefined,
 *     });
 *   }
 * };
 * ```
 */
export function createRetryAction(retryFn: () => Promise<void>): () => void {
  return () => {
    retryFn().catch(error => {
      console.error('Retry failed:', error);
    });
  };
}
